import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/dates';
import { toast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  CalendarClock,
  Route,
  MapPin,
  MapPinned,
  ArrowRightLeft,
  CheckCircle2,
  XCircle,
  Search,
  Home,
  BookmarkCheck,
  Edit3,
} from 'lucide-react';
import {
  mapboxSuggest,
  mapboxRetrieve,
  type MapboxSuggestion,
} from '@/lib/mapbox';
import { searchIvrPois, type IvrPoi } from '@/lib/ivrPois';

type AddressPrefix = 'pickup' | 'destination';

interface AddressSearchBoxProps {
  prefix: AddressPrefix;
  query: string;
  setQuery: (value: string) => void;
  suggestions: MapboxSuggestion[];
  poiSuggestions: IvrPoi[];
  searching: boolean;
  selecting: boolean;
  error?: string;
  onSelect: (prefix: AddressPrefix, suggestion: MapboxSuggestion) => void;
  onSelectPoi: (prefix: AddressPrefix, poi: IvrPoi) => void;
  onClear: (prefix: AddressPrefix) => void;
}

const AddressSearchBox: React.FC<AddressSearchBoxProps> = ({
  prefix,
  query,
  setQuery,
  suggestions,
  poiSuggestions,
  searching,
  selecting,
  error,
  onSelect,
  onSelectPoi,
  onClear,
}) => {
  return (
    <div className="space-y-1.5">
      <Label>Search Address</Label>
      <div className="relative">
        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={query}
          onChange={(e) => {
            const value = e.target.value;
            setQuery(value);
            if (!value.trim()) {
              onClear(prefix);
            }
          }}
          placeholder="Start typing address..."
          className="pl-9"
        />
        {(searching || selecting) && (
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2" />
        )}
      </div>

      {error && <p className="text-xs text-red-400">{error}</p>}

      {(poiSuggestions.length > 0 || suggestions.length > 0) && (
        <div className="rounded-lg border border-border bg-card max-h-56 overflow-y-auto">
          {poiSuggestions.length > 0 && (
            <>
              <div className="px-3 py-1 border-b border-border bg-blue-950/20">
                <p className="text-[10px] text-blue-300 uppercase tracking-wider font-medium">
                  Local Landmarks (POI)
                </p>
              </div>
              {poiSuggestions.map((poi) => (
                <button
                  key={poi.id}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-blue-900/20 border-b border-border last:border-b-0 flex items-start gap-2"
                  onClick={() => onSelectPoi(prefix, poi)}
                >
                  <MapPinned className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-foreground">{poi.name}</div>
                    {poi.notes && <div className="text-xs text-muted-foreground truncate">{poi.notes}</div>}
                  </div>
                </button>
              ))}
            </>
          )}
          {suggestions.length > 0 && (
            <>
              {poiSuggestions.length > 0 && (
                <div className="px-3 py-1 border-b border-border bg-secondary/20">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
                    Mapbox Suggestions
                  </p>
                </div>
              )}
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.mapbox_id}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-secondary/40 border-b border-border last:border-b-0"
                  onClick={() => onSelect(prefix, suggestion)}
                >
                  <div className="text-sm text-foreground">{suggestion.name || suggestion.address}</div>
                  <div className="text-xs text-muted-foreground">
                    {suggestion.full_address || suggestion.place_formatted}
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};


interface CRMAccountPermanentBookingsProps {
  accountId: string;
  accountName?: string;
}

interface PermanentBookingRow {
  id: string;
  account_id: string;
  booking_name: string;
  notes: string | null;

  applies_monday: boolean;
  applies_tuesday: boolean;
  applies_wednesday: boolean;
  applies_thursday: boolean;
  applies_friday: boolean;
  applies_saturday: boolean;
  applies_sunday: boolean;

  pickup_time: string;
  has_return_booking: boolean;
  return_time: string | null;

  pickup_label: string | null;
  pickup_full_address: string | null;
  pickup_address_line_1: string | null;
  pickup_address_line_2: string | null;
  pickup_suburb: string | null;
  pickup_city: string | null;
  pickup_postcode: string | null;
  pickup_lat: number | null;
  pickup_lng: number | null;
  pickup_place_id: string | null;

  destination_label: string | null;
  destination_full_address: string | null;
  destination_address_line_1: string | null;
  destination_address_line_2: string | null;
  destination_suburb: string | null;
  destination_city: string | null;
  destination_postcode: string | null;
  destination_lat: number | null;
  destination_lng: number | null;
  destination_place_id: string | null;

  start_date: string | null;
  expiry_date: string | null;
  is_active: boolean;

  created_at: string;
  updated_at: string;
}

type BookingFormState = {
  booking_name: string;
  notes: string;

  applies_monday: boolean;
  applies_tuesday: boolean;
  applies_wednesday: boolean;
  applies_thursday: boolean;
  applies_friday: boolean;
  applies_saturday: boolean;
  applies_sunday: boolean;

  pickup_time: string;
  has_return_booking: boolean;
  return_time: string;

  pickup_label: string;
  pickup_full_address: string;
  pickup_address_line_1: string;
  pickup_address_line_2: string;
  pickup_suburb: string;
  pickup_city: string;
  pickup_postcode: string;
  pickup_lat: string;
  pickup_lng: string;
  pickup_place_id: string;

  destination_label: string;
  destination_full_address: string;
  destination_address_line_1: string;
  destination_address_line_2: string;
  destination_suburb: string;
  destination_city: string;
  destination_postcode: string;
  destination_lat: string;
  destination_lng: string;
  destination_place_id: string;

  start_date: string;
  expiry_date: string;
  is_active: boolean;
};

const EMPTY_FORM: BookingFormState = {
  booking_name: '',
  notes: '',

  applies_monday: false,
  applies_tuesday: false,
  applies_wednesday: false,
  applies_thursday: false,
  applies_friday: false,
  applies_saturday: false,
  applies_sunday: false,

  pickup_time: '',
  has_return_booking: false,
  return_time: '',

  pickup_label: '',
  pickup_full_address: '',
  pickup_address_line_1: '',
  pickup_address_line_2: '',
  pickup_suburb: '',
  pickup_city: '',
  pickup_postcode: '',
  pickup_lat: '',
  pickup_lng: '',
  pickup_place_id: '',

  destination_label: '',
  destination_full_address: '',
  destination_address_line_1: '',
  destination_address_line_2: '',
  destination_suburb: '',
  destination_city: '',
  destination_postcode: '',
  destination_lat: '',
  destination_lng: '',
  destination_place_id: '',

  start_date: '',
  expiry_date: '',
  is_active: true,
};

const DAY_FIELDS = [
  { key: 'applies_monday', label: 'Mon' },
  { key: 'applies_tuesday', label: 'Tue' },
  { key: 'applies_wednesday', label: 'Wed' },
  { key: 'applies_thursday', label: 'Thu' },
  { key: 'applies_friday', label: 'Fri' },
  { key: 'applies_saturday', label: 'Sat' },
  { key: 'applies_sunday', label: 'Sun' },
] as const;

const formatTime = (value?: string | null) => {
  if (!value) return '-';
  return value.slice(0, 5);
};

const getDaysLabel = (row: PermanentBookingRow) => {
  const labels: string[] = [];
  if (row.applies_monday) labels.push('Mon');
  if (row.applies_tuesday) labels.push('Tue');
  if (row.applies_wednesday) labels.push('Wed');
  if (row.applies_thursday) labels.push('Thu');
  if (row.applies_friday) labels.push('Fri');
  if (row.applies_saturday) labels.push('Sat');
  if (row.applies_sunday) labels.push('Sun');
  return labels.length ? labels.join(', ') : '-';
};

const getDaysRemaining = (expiryDate?: string | null) => {
  if (!expiryDate) return null;
  const today = new Date();
  const current = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end = new Date(`${expiryDate}T00:00:00`);
  return Math.ceil((end.getTime() - current.getTime()) / 86400000);
};

interface ApprovedAddress {

  id: string;
  address_type: 'home' | 'destination';
  label: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  suburb: string | null;
  city: string | null;
  postcode: string | null;
  full_address: string | null;
  is_active: boolean;
  sort_order: number | null;
}

type AddressEntryMode = 'approved' | 'manual';

const CRMAccountPermanentBookings: React.FC<CRMAccountPermanentBookingsProps> = ({
  accountId,
  accountName,
}) => {
  const [rows, setRows] = useState<PermanentBookingRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<PermanentBookingRow | null>(null);
  const [form, setForm] = useState<BookingFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [deleteTarget, setDeleteTarget] = useState<PermanentBookingRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [pickupQuery, setPickupQuery] = useState('');
  const [destinationQuery, setDestinationQuery] = useState('');
  const [pickupSuggestions, setPickupSuggestions] = useState<MapboxSuggestion[]>([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState<MapboxSuggestion[]>([]);
  const [pickupPoiSuggestions, setPickupPoiSuggestions] = useState<IvrPoi[]>([]);
  const [destinationPoiSuggestions, setDestinationPoiSuggestions] = useState<IvrPoi[]>([]);
  const [pickupSearching, setPickupSearching] = useState(false);
  const [destinationSearching, setDestinationSearching] = useState(false);
  const [pickupSelecting, setPickupSelecting] = useState(false);
  const [destinationSelecting, setDestinationSelecting] = useState(false);

  const [pickupSessionToken, setPickupSessionToken] = useState(() => crypto.randomUUID());
  const [destinationSessionToken, setDestinationSessionToken] = useState(() => crypto.randomUUID());

  // Approved addresses from crm_account_addresses – used as quick-pick source
  const [approvedAddresses, setApprovedAddresses] = useState<ApprovedAddress[]>([]);
  const [loadingApproved, setLoadingApproved] = useState(false);

  // Per-prefix entry mode (approved quick-pick vs. manual/Mapbox entry)
  const [pickupEntryMode, setPickupEntryMode] = useState<AddressEntryMode>('approved');
  const [destinationEntryMode, setDestinationEntryMode] = useState<AddressEntryMode>('approved');

  // Track which approved address id is currently selected (for highlight)
  const [pickupSelectedApprovedId, setPickupSelectedApprovedId] = useState<string | null>(null);
  const [destinationSelectedApprovedId, setDestinationSelectedApprovedId] = useState<string | null>(null);

  // Refs used to suppress the "search" effect from firing immediately after
  // an address has been selected from the suggestion list (otherwise the
  // dropdown reopens because the query state gets updated with the chosen
  // address string).
  const skipPickupSearchRef = useRef(false);
  const skipDestinationSearchRef = useRef(false);

  const activeCount = useMemo(
    () => rows.filter((r) => r.is_active).length,
    [rows]
  );

  const hasApprovedAddresses = approvedAddresses.length > 0;


  const loadRows = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('crm_permanent_bookings')
        .select('*')
        .eq('account_id', accountId)
        .order('is_active', { ascending: false })
        .order('booking_name', { ascending: true });

      if (error) throw error;
      setRows((data || []) as PermanentBookingRow[]);
    } catch (err: any) {
      console.error('Error loading permanent bookings:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to load permanent bookings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadApprovedAddresses = async () => {
    if (!accountId) return;
    setLoadingApproved(true);
    try {
      const { data, error } = await supabase
        .from('crm_account_addresses')
        .select('id, address_type, label, address_line_1, address_line_2, suburb, city, postcode, full_address, is_active, sort_order')
        .eq('account_id', accountId)
        .eq('is_active', true)
        .order('address_type', { ascending: true })
        .order('sort_order', { ascending: true });
      if (error) throw error;
      setApprovedAddresses((data || []) as ApprovedAddress[]);
    } catch (err: any) {
      console.error('Error loading approved addresses:', err);
      setApprovedAddresses([]);
    } finally {
      setLoadingApproved(false);
    }
  };

  useEffect(() => {
    if (accountId) {
      loadRows();
      loadApprovedAddresses();
    }
  }, [accountId]);


  useEffect(() => {
    if (!dialogOpen) return;
    if (skipPickupSearchRef.current) {
      skipPickupSearchRef.current = false;
      return;
    }
    if (!pickupQuery || pickupQuery.trim().length < 2) {
      setPickupSuggestions([]);
      setPickupPoiSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setPickupSearching(true);
      try {
        // Run independently -- a Mapbox hiccup shouldn't hide POI matches
        // (our own hand-pinned landmarks) and vice versa.
        const [mapboxResult, poiResult] = await Promise.allSettled([
          mapboxSuggest(pickupQuery.trim(), pickupSessionToken),
          searchIvrPois(pickupQuery.trim()),
        ]);
        setPickupSuggestions(mapboxResult.status === 'fulfilled' ? mapboxResult.value : []);
        setPickupPoiSuggestions(poiResult.status === 'fulfilled' ? poiResult.value : []);
        if (mapboxResult.status === 'rejected') console.error('Pickup suggest error:', mapboxResult.reason);
        if (poiResult.status === 'rejected') console.error('Pickup POI search error:', poiResult.reason);
      } finally {
        setPickupSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [pickupQuery, pickupSessionToken, dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) return;
    if (skipDestinationSearchRef.current) {
      skipDestinationSearchRef.current = false;
      return;
    }
    if (!destinationQuery || destinationQuery.trim().length < 2) {
      setDestinationSuggestions([]);
      setDestinationPoiSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setDestinationSearching(true);
      try {
        const [mapboxResult, poiResult] = await Promise.allSettled([
          mapboxSuggest(destinationQuery.trim(), destinationSessionToken),
          searchIvrPois(destinationQuery.trim()),
        ]);
        setDestinationSuggestions(mapboxResult.status === 'fulfilled' ? mapboxResult.value : []);
        setDestinationPoiSuggestions(poiResult.status === 'fulfilled' ? poiResult.value : []);
        if (mapboxResult.status === 'rejected') console.error('Destination suggest error:', mapboxResult.reason);
        if (poiResult.status === 'rejected') console.error('Destination POI search error:', poiResult.reason);
      } finally {
        setDestinationSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [destinationQuery, destinationSessionToken, dialogOpen]);


  const resetForm = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setEditingRow(null);

    setPickupQuery('');
    setDestinationQuery('');
    setPickupSuggestions([]);
    setDestinationSuggestions([]);
    setPickupPoiSuggestions([]);
    setDestinationPoiSuggestions([]);
    setPickupSessionToken(crypto.randomUUID());
    setDestinationSessionToken(crypto.randomUUID());

    const defaultMode: AddressEntryMode = approvedAddresses.length > 0 ? 'approved' : 'manual';
    setPickupEntryMode(defaultMode);
    setDestinationEntryMode(defaultMode);
    setPickupSelectedApprovedId(null);
    setDestinationSelectedApprovedId(null);
  };

  const findMatchingApprovedId = (
    addressLine1: string | null,
    fullAddress: string | null,
    label: string | null
  ): string | null => {
    const l1 = (addressLine1 || '').trim().toLowerCase();
    const fa = (fullAddress || '').trim().toLowerCase();
    const lb = (label || '').trim().toLowerCase();
    for (const a of approvedAddresses) {
      const aL1 = (a.address_line_1 || '').trim().toLowerCase();
      const aFa = (a.full_address || '').trim().toLowerCase();
      const aLb = (a.label || '').trim().toLowerCase();
      if (fa && aFa && fa === aFa) return a.id;
      if (l1 && aL1 && l1 === aL1 && (lb === aLb || !lb || !aLb)) return a.id;
    }
    return null;
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (row: PermanentBookingRow) => {
    setEditingRow(row);
    setForm({
      booking_name: row.booking_name || '',
      notes: row.notes || '',

      applies_monday: row.applies_monday,
      applies_tuesday: row.applies_tuesday,
      applies_wednesday: row.applies_wednesday,
      applies_thursday: row.applies_thursday,
      applies_friday: row.applies_friday,
      applies_saturday: row.applies_saturday,
      applies_sunday: row.applies_sunday,

      pickup_time: row.pickup_time ? row.pickup_time.slice(0, 5) : '',
      has_return_booking: row.has_return_booking,
      return_time: row.return_time ? row.return_time.slice(0, 5) : '',

      pickup_label: row.pickup_label || '',
      pickup_full_address: row.pickup_full_address || '',
      pickup_address_line_1: row.pickup_address_line_1 || '',
      pickup_address_line_2: row.pickup_address_line_2 || '',
      pickup_suburb: row.pickup_suburb || '',
      pickup_city: row.pickup_city || '',
      pickup_postcode: row.pickup_postcode || '',
      pickup_lat: row.pickup_lat?.toString() || '',
      pickup_lng: row.pickup_lng?.toString() || '',
      pickup_place_id: row.pickup_place_id || '',

      destination_label: row.destination_label || '',
      destination_full_address: row.destination_full_address || '',
      destination_address_line_1: row.destination_address_line_1 || '',
      destination_address_line_2: row.destination_address_line_2 || '',
      destination_suburb: row.destination_suburb || '',
      destination_city: row.destination_city || '',
      destination_postcode: row.destination_postcode || '',
      destination_lat: row.destination_lat?.toString() || '',
      destination_lng: row.destination_lng?.toString() || '',
      destination_place_id: row.destination_place_id || '',

      start_date: row.start_date || '',
      expiry_date: row.expiry_date || '',
      is_active: row.is_active,
    });

    setPickupQuery(row.pickup_full_address || row.pickup_address_line_1 || '');
    setDestinationQuery(row.destination_full_address || row.destination_address_line_1 || '');
    setPickupSuggestions([]);
    setDestinationSuggestions([]);
    setPickupPoiSuggestions([]);
    setDestinationPoiSuggestions([]);
    setPickupSessionToken(crypto.randomUUID());
    setDestinationSessionToken(crypto.randomUUID());

    // Try to match existing booking address back to an approved address so
    // the user sees it highlighted and in "approved" mode. If no match, drop
    // into manual mode so they can freely edit the fields.
    const pickupMatch = findMatchingApprovedId(row.pickup_address_line_1, row.pickup_full_address, row.pickup_label);
    const destMatch = findMatchingApprovedId(row.destination_address_line_1, row.destination_full_address, row.destination_label);
    setPickupSelectedApprovedId(pickupMatch);
    setDestinationSelectedApprovedId(destMatch);
    setPickupEntryMode(pickupMatch ? 'approved' : 'manual');
    setDestinationEntryMode(destMatch ? 'approved' : 'manual');

    setErrors({});
    setDialogOpen(true);
  };

  const applyApprovedAddress = (prefix: AddressPrefix, addr: ApprovedAddress) => {
    setForm((prev) => ({
      ...prev,
      [`${prefix}_label`]: addr.label || (addr.address_type === 'home' ? 'Home' : ''),
      [`${prefix}_full_address`]: addr.full_address || '',
      [`${prefix}_address_line_1`]: addr.address_line_1 || '',
      [`${prefix}_address_line_2`]: addr.address_line_2 || '',
      [`${prefix}_suburb`]: addr.suburb || '',
      [`${prefix}_city`]: addr.city || '',
      [`${prefix}_postcode`]: addr.postcode || '',
      [`${prefix}_lat`]: '',
      [`${prefix}_lng`]: '',
      [`${prefix}_place_id`]: '',
    }) as BookingFormState);

    if (prefix === 'pickup') {
      setPickupSelectedApprovedId(addr.id);
      setPickupQuery(addr.full_address || addr.address_line_1 || '');
    } else {
      setDestinationSelectedApprovedId(addr.id);
      setDestinationQuery(addr.full_address || addr.address_line_1 || '');
    }

    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${prefix}_full_address`];
      return next;
    });
  };

  const switchEntryMode = (prefix: AddressPrefix, mode: AddressEntryMode) => {
    if (prefix === 'pickup') {
      setPickupEntryMode(mode);
      if (mode === 'manual') {
        // Leave the currently chosen fields in place so they can tweak them;
        // just drop the "selected approved" highlight.
        setPickupSelectedApprovedId(null);
      }
    } else {
      setDestinationEntryMode(mode);
      if (mode === 'manual') {
        setDestinationSelectedApprovedId(null);
      }
    }
  };


  const updateField = (field: keyof BookingFormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value } as BookingFormState));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const clearAddressFields = (prefix: AddressPrefix) => {
    setForm((prev) => ({
      ...prev,
      [`${prefix}_label`]: '',
      [`${prefix}_full_address`]: '',
      [`${prefix}_address_line_1`]: '',
      [`${prefix}_address_line_2`]: '',
      [`${prefix}_suburb`]: '',
      [`${prefix}_city`]: '',
      [`${prefix}_postcode`]: '',
      [`${prefix}_lat`]: '',
      [`${prefix}_lng`]: '',
      [`${prefix}_place_id`]: '',
    }) as BookingFormState);
  };

  const handleAddressSelected = async (prefix: AddressPrefix, suggestion: MapboxSuggestion) => {
    const isPickup = prefix === 'pickup';
    const setSelecting = isPickup ? setPickupSelecting : setDestinationSelecting;
    const setSuggestions = isPickup ? setPickupSuggestions : setDestinationSuggestions;
    const sessionToken = isPickup ? pickupSessionToken : destinationSessionToken;
    const setQuery = isPickup ? setPickupQuery : setDestinationQuery;
    const setSession = isPickup ? setPickupSessionToken : setDestinationSessionToken;
    const skipRef = isPickup ? skipPickupSearchRef : skipDestinationSearchRef;

    try {
      setSelecting(true);
      const retrieved = await mapboxRetrieve(suggestion.mapbox_id, sessionToken);

      setForm((prev) => ({
        ...prev,
        [`${prefix}_label`]: suggestion.name || '',
        [`${prefix}_full_address`]: retrieved.full_address || suggestion.full_address || '',
        [`${prefix}_address_line_1`]: retrieved.address_line_1 || '',
        [`${prefix}_address_line_2`]: retrieved.address_line_2 || '',
        [`${prefix}_suburb`]: retrieved.suburb || '',
        [`${prefix}_city`]: retrieved.city || '',
        [`${prefix}_postcode`]: retrieved.postcode || '',
        [`${prefix}_place_id`]: suggestion.mapbox_id || '',
      }) as BookingFormState);

      // Prevent the debounce effect from re-opening the dropdown when we
      // programmatically update the query with the selected address string.
      skipRef.current = true;
      setQuery(retrieved.full_address || suggestion.full_address || suggestion.address || '');
      setSuggestions([]);
      setSession(crypto.randomUUID());

      setErrors((prev) => {
        const next = { ...prev };
        delete next[`${prefix}_full_address`];
        return next;
      });
    } catch (err: any) {
      console.error(`Error selecting ${prefix} address:`, err);
      toast({
        title: 'Address Lookup Failed',
        description: err.message || 'Could not retrieve address details',
        variant: 'destructive',
      });
    } finally {
      setSelecting(false);
    }
  };

  // POI selection -- coordinates already on file (hand-pinned by the IVR's
  // own landmark list), so this skips the Mapbox retrieve call entirely.
  // There's no suburb/city/postcode on a POI row, so those are cleared
  // rather than left stale from whatever was typed before.
  const handlePoiSelected = (prefix: AddressPrefix, poi: IvrPoi) => {
    const isPickup = prefix === 'pickup';
    const setSuggestions = isPickup ? setPickupSuggestions : setDestinationSuggestions;
    const setPoiSuggestions = isPickup ? setPickupPoiSuggestions : setDestinationPoiSuggestions;
    const setQuery = isPickup ? setPickupQuery : setDestinationQuery;
    const setSession = isPickup ? setPickupSessionToken : setDestinationSessionToken;
    const skipRef = isPickup ? skipPickupSearchRef : skipDestinationSearchRef;

    setForm((prev) => ({
      ...prev,
      [`${prefix}_label`]: poi.name,
      [`${prefix}_full_address`]: poi.name,
      [`${prefix}_address_line_1`]: poi.name,
      [`${prefix}_address_line_2`]: '',
      [`${prefix}_suburb`]: '',
      [`${prefix}_city`]: '',
      [`${prefix}_postcode`]: '',
      [`${prefix}_lat`]: String(poi.lat),
      [`${prefix}_lng`]: String(poi.lon),
      [`${prefix}_place_id`]: '',
    }) as BookingFormState);

    skipRef.current = true;
    setQuery(poi.name);
    setSuggestions([]);
    setPoiSuggestions([]);
    setSession(crypto.randomUUID());

    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${prefix}_full_address`];
      return next;
    });
  };


  const validate = () => {
    const nextErrors: Record<string, string> = {};

    if (!form.booking_name.trim()) {
      nextErrors.booking_name = 'Booking name is required';
    }

    if (!form.pickup_time) {
      nextErrors.pickup_time = 'Pickup time is required';
    }

    const daySelected = DAY_FIELDS.some((day) => form[day.key]);
    if (!daySelected) {
      nextErrors.days = 'Select at least one day';
    }

    if (form.has_return_booking && !form.return_time) {
      nextErrors.return_time = 'Return time is required when return booking is enabled';
    }

    if (
      form.start_date &&
      form.expiry_date &&
      new Date(`${form.expiry_date}T00:00:00`) < new Date(`${form.start_date}T00:00:00`)
    ) {
      nextErrors.expiry_date = 'Expiry date cannot be before start date';
    }

    if (!form.pickup_full_address.trim() && !form.pickup_address_line_1.trim()) {
      nextErrors.pickup_full_address = 'Pickup address is required';
    }

    if (!form.destination_full_address.trim() && !form.destination_address_line_1.trim()) {
      nextErrors.destination_full_address = 'Destination address is required';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => {
    const toNumberOrNull = (value: string) => {
      if (!value.trim()) return null;
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };

    return {
      account_id: accountId,
      booking_name: form.booking_name.trim(),
      notes: form.notes.trim() || null,

      applies_monday: form.applies_monday,
      applies_tuesday: form.applies_tuesday,
      applies_wednesday: form.applies_wednesday,
      applies_thursday: form.applies_thursday,
      applies_friday: form.applies_friday,
      applies_saturday: form.applies_saturday,
      applies_sunday: form.applies_sunday,

      pickup_time: form.pickup_time,
      has_return_booking: form.has_return_booking,
      return_time: form.has_return_booking ? form.return_time || null : null,

      pickup_label: form.pickup_label.trim() || null,
      pickup_full_address: form.pickup_full_address.trim() || null,
      pickup_address_line_1: form.pickup_address_line_1.trim() || null,
      pickup_address_line_2: form.pickup_address_line_2.trim() || null,
      pickup_suburb: form.pickup_suburb.trim() || null,
      pickup_city: form.pickup_city.trim() || null,
      pickup_postcode: form.pickup_postcode.trim() || null,
      pickup_lat: toNumberOrNull(form.pickup_lat),
      pickup_lng: toNumberOrNull(form.pickup_lng),
      pickup_place_id: form.pickup_place_id.trim() || null,

      destination_label: form.destination_label.trim() || null,
      destination_full_address: form.destination_full_address.trim() || null,
      destination_address_line_1: form.destination_address_line_1.trim() || null,
      destination_address_line_2: form.destination_address_line_2.trim() || null,
      destination_suburb: form.destination_suburb.trim() || null,
      destination_city: form.destination_city.trim() || null,
      destination_postcode: form.destination_postcode.trim() || null,
      destination_lat: toNumberOrNull(form.destination_lat),
      destination_lng: toNumberOrNull(form.destination_lng),
      destination_place_id: form.destination_place_id.trim() || null,

      start_date: form.start_date || null,
      expiry_date: form.expiry_date || null,
      is_active: form.is_active,
    };
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = buildPayload();

      if (editingRow) {
        const { error } = await supabase
          .from('crm_permanent_bookings')
          .update(payload)
          .eq('id', editingRow.id);

        if (error) throw error;

        toast({
          title: 'Permanent Booking Updated',
          description: `${payload.booking_name} has been updated.`,
        });
      } else {
        const { error } = await supabase
          .from('crm_permanent_bookings')
          .insert(payload);

        if (error) throw error;

        toast({
          title: 'Permanent Booking Added',
          description: `${payload.booking_name} has been created.`,
        });
      }

      setDialogOpen(false);
      resetForm();
      await loadRows();
    } catch (err: any) {
      console.error('Error saving permanent booking:', err);
      toast({
        title: 'Save Failed',
        description: err.message || 'Failed to save permanent booking',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      const { error } = await supabase
        .from('crm_permanent_bookings')
        .delete()
        .eq('id', deleteTarget.id);

      if (error) throw error;

      toast({
        title: 'Permanent Booking Deleted',
        description: `${deleteTarget.booking_name} has been removed.`,
      });

      setDeleteTarget(null);
      await loadRows();
    } catch (err: any) {
      console.error('Error deleting permanent booking:', err);
      toast({
        title: 'Delete Failed',
        description: err.message || 'Failed to delete permanent booking',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };



  return (
    <>
      <Card className="bg-secondary/30 border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <CalendarClock className="w-4 h-4" />
                Permanent Bookings
                <Badge variant="secondary" className="text-xs">
                  {activeCount} active
                </Badge>
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Recurring bookings for {accountName || accountId}
              </p>
            </div>

            <Button className="golden-button" size="sm" onClick={openCreate}>
              <Plus className="w-4 h-4 mr-1.5" />
              Add Permanent Booking
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Loading permanent bookings...
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-10 space-y-2">
              <CalendarClock className="w-8 h-8 mx-auto text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">
                No permanent bookings set up yet.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-xs text-muted-foreground">Name</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Days</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Pickup</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Return</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Route</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Expiry</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Days Left</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Status</TableHead>
                    <TableHead className="text-xs text-muted-foreground text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {rows.map((row) => {
                    const daysRemaining = getDaysRemaining(row.expiry_date);
                    const pickupAddress =
                      row.pickup_label ||
                      row.pickup_full_address ||
                      row.pickup_address_line_1 ||
                      '-';
                    const destinationAddress =
                      row.destination_label ||
                      row.destination_full_address ||
                      row.destination_address_line_1 ||
                      '-';

                    return (
                      <TableRow key={row.id} className="border-border">
                        <TableCell>
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {row.booking_name}
                            </p>
                            {row.notes && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {row.notes}
                              </p>
                            )}
                          </div>
                        </TableCell>

                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {getDaysLabel(row)}
                        </TableCell>

                        <TableCell className="text-xs whitespace-nowrap">
                          {formatTime(row.pickup_time)}
                        </TableCell>

                        <TableCell className="text-xs whitespace-nowrap">
                          {row.has_return_booking ? formatTime(row.return_time) : '-'}
                        </TableCell>

                        <TableCell>
                          <div className="min-w-[260px] space-y-1">
                            <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                              <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-1">{pickupAddress}</span>
                            </div>
                            <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                              <Route className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-1">{destinationAddress}</span>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell className="text-xs whitespace-nowrap">
                          {formatDate(row.expiry_date)}
                        </TableCell>

                        <TableCell className="text-xs whitespace-nowrap">
                          {daysRemaining === null ? (
                            <span className="text-muted-foreground">-</span>
                          ) : daysRemaining < 0 ? (
                            <span className="text-red-400 font-medium">
                              Expired {Math.abs(daysRemaining)}d
                            </span>
                          ) : (
                            <span className="text-muted-foreground">{daysRemaining}d</span>
                          )}
                        </TableCell>

                        <TableCell>
                          {row.is_active ? (
                            <Badge className="bg-green-900/40 text-green-400 border-green-700/50">
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-secondary text-muted-foreground border-border">
                              Inactive
                            </Badge>
                          )}
                        </TableCell>

                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-yellow-600 text-yellow-400 hover:bg-yellow-900/30"
                              onClick={() => openEdit(row)}
                            >
                              <Pencil className="w-3.5 h-3.5 mr-1.5" />
                              Edit
                            </Button>

                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-600/60 text-red-400 hover:bg-red-900/30"
                              onClick={() => setDeleteTarget(row)}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col bg-card border-border">
          <DialogHeader>
            <DialogTitle className="golden-text">
              {editingRow ? 'Edit Permanent Booking' : 'Add Permanent Booking'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-6 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Booking Name</Label>
                <Input
                  value={form.booking_name}
                  onChange={(e) => updateField('booking_name', e.target.value)}
                  placeholder="e.g. Dialysis Morning Run"
                />
                {errors.booking_name && (
                  <p className="text-xs text-red-400">{errors.booking_name}</p>
                )}
              </div>

              <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/20 px-3 py-3">
                <div>
                  <Label className="text-sm">Active</Label>
                  <p className="text-xs text-muted-foreground">
                    Whether this permanent booking is currently in use
                  </p>
                </div>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(checked) => updateField('is_active', checked)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Applies On</Label>
              <div className="flex flex-wrap gap-2">
                {DAY_FIELDS.map((day) => (
                  <Button
                    key={day.key}
                    type="button"
                    variant={form[day.key] ? 'default' : 'outline'}
                    className={form[day.key] ? 'golden-button' : 'border-border text-muted-foreground'}
                    onClick={() => updateField(day.key, !form[day.key])}
                  >
                    {form[day.key] ? (
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 mr-1.5 opacity-50" />
                    )}
                    {day.label}
                  </Button>
                ))}
              </div>
              {errors.days && <p className="text-xs text-red-400">{errors.days}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label>Pickup Time</Label>
                <Input
                  type="time"
                  value={form.pickup_time}
                  onChange={(e) => updateField('pickup_time', e.target.value)}
                />
                {errors.pickup_time && (
                  <p className="text-xs text-red-400">{errors.pickup_time}</p>
                )}
              </div>

              <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/20 px-3 py-3 md:col-span-1">
                <div>
                  <Label className="text-sm">Return Booking</Label>
                  <p className="text-xs text-muted-foreground">Does this include a return trip?</p>
                </div>
                <Switch
                  checked={form.has_return_booking}
                  onCheckedChange={(checked) => {
                    updateField('has_return_booking', checked);
                    if (!checked) updateField('return_time', '');
                  }}
                />
              </div>

              <div className="space-y-1.5 md:col-span-2">
                <Label>Return Time</Label>
                <Input
                  type="time"
                  value={form.return_time}
                  disabled={!form.has_return_booking}
                  onChange={(e) => updateField('return_time', e.target.value)}
                />
                {errors.return_time && (
                  <p className="text-xs text-red-400">{errors.return_time}</p>
                )}
              </div>
            </div>

            {/* ═══════════════ PICKUP ADDRESS ═══════════════ */}
            {(() => {
              const prefix: AddressPrefix = 'pickup';
              const mode = pickupEntryMode;
              const selectedId = pickupSelectedApprovedId;
              return (
                <Card className="bg-secondary/20 border-border">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Pickup Address
                      </CardTitle>

                      {hasApprovedAddresses && (
                        <div className="inline-flex rounded-md border border-border bg-card p-0.5">
                          <button
                            type="button"
                            className={`text-xs px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors ${
                              mode === 'approved'
                                ? 'bg-yellow-900/30 text-yellow-400'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                            onClick={() => switchEntryMode(prefix, 'approved')}
                          >
                            <BookmarkCheck className="w-3.5 h-3.5" />
                            Approved ({approvedAddresses.length})
                          </button>
                          <button
                            type="button"
                            className={`text-xs px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors ${
                              mode === 'manual'
                                ? 'bg-yellow-900/30 text-yellow-400'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                            onClick={() => switchEntryMode(prefix, 'manual')}
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                            Enter manually
                          </button>
                        </div>
                      )}
                    </div>
                    {!hasApprovedAddresses && !loadingApproved && (
                      <p className="text-xs text-muted-foreground mt-1">
                        No approved addresses on file — enter the pickup address below.
                      </p>
                    )}
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {mode === 'approved' && hasApprovedAddresses ? (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {approvedAddresses.map((addr) => {
                            const isSelected = selectedId === addr.id;
                            const isHome = addr.address_type === 'home';
                            return (
                              <button
                                key={addr.id}
                                type="button"
                                onClick={() => applyApprovedAddress(prefix, addr)}
                                className={`text-left rounded-lg border px-3 py-2.5 transition-colors flex items-start gap-2.5 ${
                                  isSelected
                                    ? 'border-yellow-500/70 bg-yellow-900/15 ring-1 ring-yellow-500/40'
                                    : 'border-border bg-secondary/30 hover:bg-secondary/50 hover:border-yellow-600/40'
                                }`}
                              >
                                <div className={`flex-shrink-0 mt-0.5 ${isHome ? 'text-blue-400' : 'text-yellow-400'}`}>
                                  {isHome ? <Home className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5">
                                    <span className={`text-sm font-medium ${isHome ? 'text-blue-300' : 'text-yellow-300'}`}>
                                      {addr.label || (isHome ? 'Home' : 'Destination')}
                                    </span>
                                    {isSelected && (
                                      <CheckCircle2 className="w-3.5 h-3.5 text-yellow-400" />
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground line-clamp-2">
                                    {addr.full_address ||
                                      [addr.address_line_1, addr.suburb, addr.city, addr.postcode]
                                        .filter(Boolean)
                                        .join(', ')}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        {selectedId && form.pickup_full_address && (
                          <div className="space-y-1.5">
                            <Label>Selected Pickup</Label>
                            <Input value={form.pickup_full_address} readOnly className="bg-secondary/40" />
                          </div>
                        )}

                        {errors.pickup_full_address && (
                          <p className="text-xs text-red-400">{errors.pickup_full_address}</p>
                        )}
                      </>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <AddressSearchBox
                          prefix="pickup"
                          query={pickupQuery}
                          setQuery={setPickupQuery}
                          suggestions={pickupSuggestions}
                          poiSuggestions={pickupPoiSuggestions}
                          searching={pickupSearching}
                          selecting={pickupSelecting}
                          error={errors.pickup_full_address}
                          onSelect={handleAddressSelected}
                          onSelectPoi={handlePoiSelected}
                          onClear={clearAddressFields}
                        />

                        <div className="space-y-1.5">
                          <Label>Label</Label>
                          <Input
                            value={form.pickup_label}
                            onChange={(e) => updateField('pickup_label', e.target.value)}
                            placeholder="e.g. Home"
                          />
                        </div>

                        <div className="space-y-1.5 md:col-span-2">
                          <Label>Full Address</Label>
                          <Input
                            value={form.pickup_full_address}
                            onChange={(e) => updateField('pickup_full_address', e.target.value)}
                            placeholder="Selected full address"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>Address Line 1</Label>
                          <Input
                            value={form.pickup_address_line_1}
                            onChange={(e) => updateField('pickup_address_line_1', e.target.value)}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>Address Line 2</Label>
                          <Input
                            value={form.pickup_address_line_2}
                            onChange={(e) => updateField('pickup_address_line_2', e.target.value)}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>Suburb</Label>
                          <Input
                            value={form.pickup_suburb}
                            onChange={(e) => updateField('pickup_suburb', e.target.value)}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>City</Label>
                          <Input
                            value={form.pickup_city}
                            onChange={(e) => updateField('pickup_city', e.target.value)}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>Postcode</Label>
                          <Input
                            value={form.pickup_postcode}
                            onChange={(e) => updateField('pickup_postcode', e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* ═══════════════ DESTINATION ADDRESS ═══════════════ */}
            {(() => {
              const prefix: AddressPrefix = 'destination';
              const mode = destinationEntryMode;
              const selectedId = destinationSelectedApprovedId;
              return (
                <Card className="bg-secondary/20 border-border">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                        <Route className="w-4 h-4" />
                        Destination Address
                      </CardTitle>

                      {hasApprovedAddresses && (
                        <div className="inline-flex rounded-md border border-border bg-card p-0.5">
                          <button
                            type="button"
                            className={`text-xs px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors ${
                              mode === 'approved'
                                ? 'bg-yellow-900/30 text-yellow-400'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                            onClick={() => switchEntryMode(prefix, 'approved')}
                          >
                            <BookmarkCheck className="w-3.5 h-3.5" />
                            Approved ({approvedAddresses.length})
                          </button>
                          <button
                            type="button"
                            className={`text-xs px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors ${
                              mode === 'manual'
                                ? 'bg-yellow-900/30 text-yellow-400'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                            onClick={() => switchEntryMode(prefix, 'manual')}
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                            Enter manually
                          </button>
                        </div>
                      )}
                    </div>
                    {!hasApprovedAddresses && !loadingApproved && (
                      <p className="text-xs text-muted-foreground mt-1">
                        No approved addresses on file — enter the destination address below.
                      </p>
                    )}
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {mode === 'approved' && hasApprovedAddresses ? (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {approvedAddresses.map((addr) => {
                            const isSelected = selectedId === addr.id;
                            const isHome = addr.address_type === 'home';
                            return (
                              <button
                                key={addr.id}
                                type="button"
                                onClick={() => applyApprovedAddress(prefix, addr)}
                                className={`text-left rounded-lg border px-3 py-2.5 transition-colors flex items-start gap-2.5 ${
                                  isSelected
                                    ? 'border-yellow-500/70 bg-yellow-900/15 ring-1 ring-yellow-500/40'
                                    : 'border-border bg-secondary/30 hover:bg-secondary/50 hover:border-yellow-600/40'
                                }`}
                              >
                                <div className={`flex-shrink-0 mt-0.5 ${isHome ? 'text-blue-400' : 'text-yellow-400'}`}>
                                  {isHome ? <Home className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5">
                                    <span className={`text-sm font-medium ${isHome ? 'text-blue-300' : 'text-yellow-300'}`}>
                                      {addr.label || (isHome ? 'Home' : 'Destination')}
                                    </span>
                                    {isSelected && (
                                      <CheckCircle2 className="w-3.5 h-3.5 text-yellow-400" />
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground line-clamp-2">
                                    {addr.full_address ||
                                      [addr.address_line_1, addr.suburb, addr.city, addr.postcode]
                                        .filter(Boolean)
                                        .join(', ')}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        {selectedId && form.destination_full_address && (
                          <div className="space-y-1.5">
                            <Label>Selected Destination</Label>
                            <Input value={form.destination_full_address} readOnly className="bg-secondary/40" />
                          </div>
                        )}

                        {errors.destination_full_address && (
                          <p className="text-xs text-red-400">{errors.destination_full_address}</p>
                        )}
                      </>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <AddressSearchBox
                          prefix="destination"
                          query={destinationQuery}
                          setQuery={setDestinationQuery}
                          suggestions={destinationSuggestions}
                          poiSuggestions={destinationPoiSuggestions}
                          searching={destinationSearching}
                          selecting={destinationSelecting}
                          error={errors.destination_full_address}
                          onSelect={handleAddressSelected}
                          onSelectPoi={handlePoiSelected}
                          onClear={clearAddressFields}
                        />

                        <div className="space-y-1.5">
                          <Label>Label</Label>
                          <Input
                            value={form.destination_label}
                            onChange={(e) => updateField('destination_label', e.target.value)}
                            placeholder="e.g. Dialysis Unit"
                          />
                        </div>

                        <div className="space-y-1.5 md:col-span-2">
                          <Label>Full Address</Label>
                          <Input
                            value={form.destination_full_address}
                            onChange={(e) => updateField('destination_full_address', e.target.value)}
                            placeholder="Selected full address"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>Address Line 1</Label>
                          <Input
                            value={form.destination_address_line_1}
                            onChange={(e) => updateField('destination_address_line_1', e.target.value)}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>Address Line 2</Label>
                          <Input
                            value={form.destination_address_line_2}
                            onChange={(e) => updateField('destination_address_line_2', e.target.value)}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>Suburb</Label>
                          <Input
                            value={form.destination_suburb}
                            onChange={(e) => updateField('destination_suburb', e.target.value)}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>City</Label>
                          <Input
                            value={form.destination_city}
                            onChange={(e) => updateField('destination_city', e.target.value)}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label>Postcode</Label>
                          <Input
                            value={form.destination_postcode}
                            onChange={(e) => updateField('destination_postcode', e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}



            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => updateField('start_date', e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={form.expiry_date}
                  onChange={(e) => updateField('expiry_date', e.target.value)}
                />
                {errors.expiry_date && (
                  <p className="text-xs text-red-400">{errors.expiry_date}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  className="min-h-[40px]"
                  placeholder="Anything dispatch should know..."
                />
              </div>
            </div>

            {form.has_return_booking && (
              <div className="rounded-lg border border-blue-700/40 bg-blue-900/10 px-3 py-3 text-sm text-blue-400 flex items-center gap-2">
                <ArrowRightLeft className="w-4 h-4" />
                This booking includes a return leg.
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 border-t border-border pt-4">
            <Button
              variant="outline"
              className="border-border text-muted-foreground"
              onClick={() => {
                setDialogOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>

            <Button className="golden-button" onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-1.5" />
              )}
              {editingRow ? 'Update Booking' : 'Create Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Permanent Booking
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-sm text-foreground">
              Are you sure you want to delete this permanent booking?
            </p>

            {deleteTarget && (
              <div className="rounded-lg border border-red-700/40 bg-red-900/10 p-3">
                <p className="text-sm font-semibold text-foreground">
                  {deleteTarget.booking_name}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {getDaysLabel(deleteTarget)} • {formatTime(deleteTarget.pickup_time)}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="border-border text-muted-foreground"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </Button>

            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-1.5" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CRMAccountPermanentBookings;